import {BooleanRenderer, ESBA_DATE_FORMAT} from 'common';
import {differenceInBusinessDays, format, parseISO} from 'date-fns';
import {FormField, ListItem, Paragraph, Pre, Text, UnorderedList} from 'evergreen-ui';
import pluralize from 'pluralize';
import React, {memo, ReactNode} from 'react';
import {isEmpty, isNumber} from 'lodash';
import {
    includedSocialOrGroupABA,
    isCalibrateOrBehavioralOrRUBI,
    isClinicRequired,
    isLanguageRequired,
    isPsychotherapyOfferRequired,
    isTreatmentHourRequired,
    isTreatmentOfferedCaregiverAndPractitioner,
    isTreatmentOfferedCareMedicated,
    isTreatmentOfferedPractitionerMedicated,
    Referral,
    ServiceType,
    TreatmentOffered,
    treatmentOfferedToLabel
} from 'referral';
import {
    CaregiverLocationDisplay,
    caregiverLocationDisplay,
    maxHoursForOffer,
    offerCaregiverLocations,
    RequiredHourFields,
    shouldRequireClinicInputBySelectedLocation
} from '../domain/offer';
import {extractClinicLocationToDisplay, Offer, ServiceLocationTypeMap} from '../index';
import {splitAndRemoveEmpty} from "../../placement/const";


type HourViewProps = {
    label: string;
    actualHour: number | null;
    expectedHour: number | null;
}
const HourView = ({label, actualHour, expectedHour}: HourViewProps) => {
    const userHours = (actualHour !== null && isNumber(actualHour) && isFinite(actualHour)) ? actualHour : undefined;
    const authHours = (expectedHour !== null && isNumber(expectedHour) && isFinite(expectedHour)) ? expectedHour : undefined;
    return (
        <Paragraph display='flex' maxWidth={'48em'} marginY={16}>
            <Text width='60%'>{label}</Text>
            {userHours !== undefined && <Text width='20%'>{userHours} {pluralize('hour', userHours)}</Text>}
            {authHours !== undefined && <Text width='40%'>({authHours} {pluralize('hour', authHours)} auth.)</Text>}
        </Paragraph>
    );
}

type PsychotherapyUnitViewProps = {
    label: string;
    actualOffer: number | null;
    expectedOffer: number | null;
    unitType: string;
    unitPer: string
}
const PsychotherapyUnitView = ({label, actualOffer, expectedOffer, unitType, unitPer}: PsychotherapyUnitViewProps) => {
    const userHours = (actualOffer !== null && isNumber(actualOffer) && isFinite(actualOffer)) ? actualOffer : undefined;
    const authHours = (expectedOffer !== null && isNumber(expectedOffer) && isFinite(expectedOffer)) ? expectedOffer : undefined;
    return (
        <Paragraph display='flex' width='48em' marginY={16}>
            <Text width='30%' fontWeight={500}>{label}</Text>
            {userHours !== undefined &&
                <Text width='30%'>{userHours} {pluralize(unitType, userHours)} per {unitPer} </Text>}
            {authHours !== undefined &&
                <Text width='40%'>({authHours} {pluralize(unitType, authHours)} per {unitPer} auth.)</Text>}
        </Paragraph>
    );
}


type DateViewProps = {
    label: ReactNode;
    dateStr: string | null;
    showDaysFromNow?: boolean;
}
const DateView = ({dateStr, label, showDaysFromNow = true}: DateViewProps) => {
    const date = !!dateStr ? parseISO(dateStr) : null;
    const daysFromNow = date ? differenceInBusinessDays(date, new Date()) : -1;
    return (
        <FormField label={label} marginY={16}>
            <Text>{date ? format(date, ESBA_DATE_FORMAT) : ''}</Text>
            {daysFromNow >= 0 && showDaysFromNow &&
                <Text marginLeft={8}>
                    in {<b>{daysFromNow}</b>} business day(s)
                </Text>
            }
        </FormField>
    );
}

type TreatmentOfferedViewProp = {
    offer: Offer;
}

const TreatmentOfferedView = ({offer}: TreatmentOfferedViewProp) => {
    return (
        <FormField label={"Treatment Offered"} marginY={16}>
            <Text>
                {treatmentOfferedToLabel(offer.treatmentOffered as TreatmentOffered)}
            </Text>
        </FormField>
    );
}

const SERVICE_IN_LANGUAGE_MAPPING = {
    'true': 'Yes',
    'false': 'No, interpreter required',
};

type Props = {
    referral: Referral;
    offer: Offer;
}

export const OfferDetails = memo(function OfferDetails({referral, offer}: Props) {
    const referralHours = maxHoursForOffer(referral);
    const shouldShowGroupCareHours = includedSocialOrGroupABA(referral.serviceTypes);
    const requireClinicLocationInput = shouldRequireClinicInputBySelectedLocation(isClinicRequired(referral), [
        offer.serviceLocationType,
        offer.serviceLocationParaprofessionalType,
        offer.serviceLocationMidLevelType,
        offer.serviceLocationHighLevelType,
        offer.serviceLocationGroupCareType
    ])
    const locationCond = caregiverLocationDisplay(
        offerCaregiverLocations(offer),
        offer.treatmentOffered);
    return (
        <div className="w-full py-2">
            {
                locationCond.showAssessment &&
                <FormField label='Service Location' marginY={16}>
                    <UnorderedList>
                        {splitAndRemoveEmpty(offer.serviceLocationType)
                            .map((t) =>
                                <ListItem key={t}>
                                    <Text>{ServiceLocationTypeMap.get(t)}</Text>
                                </ListItem>)}

                    </UnorderedList>
                </FormField>
            }
            {isClinicRequired(referral, requireClinicLocationInput) &&
                <>
                    <FormField label='Can you provide services in a clinic settings' marginY={16}>
                        <Text><BooleanRenderer value={offer.provideServicesInClinic}/></Text>
                    </FormField>
                    <FormField label='Clinic Location' marginY={16}>
                        <Text>{extractClinicLocationToDisplay(offer).name}</Text>
                    </FormField>
                    <FormField label='Other Location' marginY={16}>
                        <Text>{offer.otherClinicLocation}</Text>
                    </FormField>
                </>
            }

            {isLanguageRequired(referral) &&
                <FormField label={`Can your team provide services in "${referral.requiredLanguage}"?`} marginY={16}>
                    <Text>
                        <BooleanRenderer value={offer.provideServiceInRequiredLanguage}
                                         customLabel={SERVICE_IN_LANGUAGE_MAPPING}/>
                    </Text>
                </FormField>
            }

            <DateView label='What is the first date you can offer services?' dateStr={offer.firstServiceDate}/>

            {isPsychotherapyOfferRequired(referral) && <>
                {psychotherapyOfferRender(offer, referral)}
            </>}

            {(isTreatmentHourRequired(referral)) &&
                <>
                    <TreatmentOfferedView offer={offer}/>
                    {nonMedicatedHoursRender(offer, referral, referralHours)}
                    {caredMedicatedHoursRender(offer, referral, referralHours, locationCond)}
                    {practitionerMedicatedHoursRender(offer, referral, referralHours, shouldShowGroupCareHours, !isTreatmentOfferedPractitionerMedicated(offer.treatmentOffered))}
                    {caregiverAndPractionerMedicatedRender(offer, referralHours, referral, shouldShowGroupCareHours)}
                </>
            }
            {
                staffingPlanRender(offer)
            }


            <FormField label='Comments' marginTop={8}>
                <Pre wordBreak='break-word' whiteSpace='break-spaces' wordWrap='break-word'>
                    {offer.comments}
                </Pre>
            </FormField>
        </div>
    );
});

const psychotherapyOfferRender = (offer: Offer, referral: Referral) => {
    return (
        <div>
            <PsychotherapyUnitView label='Offer:'
                                   actualOffer={offer.psychotherapyOffer}
                                   expectedOffer={referral.authTherapyTreatmentUnit}
                                   unitPer={referral.authTherapyTreatmentUnitsPer}
                                   unitType={referral.authTherapyTreatmentUnitType}
            />
        </div>
    )
};

const nonMedicatedHoursRender = (offer: Offer, referral: Referral, referralHours: Record<RequiredHourFields, number>) => {
    // BHPNPP-1944
    // from now on, the 3 services will have preset Treatment Offered
    // for older offers, they won't have Treatment Offered, so we will display with this section
    if (!(isCalibrateOrBehavioralOrRUBI(referral.serviceTypes) && isEmpty(offer.treatmentOffered))) {
        return null
    }

    return (
        <div>
            {
                referral.serviceTypes.includes(ServiceType.Calibrate) &&
                <HourView label='Mid-level Hours:'
                          actualHour={offer.midLevelSupervisionHours}
                          expectedHour={referralHours.midLevelHours}
                />
            }
            <HourView label='High-level Hours:'
                      actualHour={offer.highLevelSupervisionHours}
                      expectedHour={referralHours.highLevelHours}
            />
        </div>
    );
}

// A two-column grid, the same one the practitioner block below uses. Hours on the left, the
// location that goes with them on the right. The locations need no label of their own, because
// the Hours label beside each one already names the level.
const caredMedicatedHoursRender = (offer: Offer, referral: Referral, referralHours: Record<RequiredHourFields, number>, locationCond: CaregiverLocationDisplay) => {
    if (!isTreatmentOfferedCareMedicated(offer.treatmentOffered)) {
        return null;
    }
    const isNotRubi = !(referral?.serviceTypes ?? []).includes(ServiceType.RUBI);
    const showAnyLocation = locationCond.showMidLevel || locationCond.showHighLevel;

    return (
        <div className={"grid grid-cols-2"}>
            <DateView label='Start Date Caregiver Mediated'
                      dateStr={offer.startDateCaregiverMediatedTreatment}/>
            {/* The cell stays even when the header is gone, so the rows below keep their pairing. */}
            <div>
                {showAnyLocation && <FormField label='Service Location' paddingY={16}/>}
            </div>
            {
                // RUBI has no mid-level supervision, so neither the hours nor the location exist.
                isNotRubi && (<>
                    <HourView label='Mid-Level Supervision Hours:'
                              actualHour={offer.caregiverMediatedMidLevelHours}
                              expectedHour={referralHours.careMediatedMidHours}
                    />
                    <div className={'flex items-center'}>
                        {locationCond.showMidLevel &&
                            <Text>{ServiceLocationTypeMap.get(offer.serviceLocationMidLevelType)}</Text>}
                    </div>
                </>)
            }
            <HourView label='High-Level Supervision Hours:'
                      actualHour={offer.caregiverMediatedHighLevelHours}
                      expectedHour={referralHours.careMediatedHighHours}
            />
            <div className={'flex items-center'}>
                {locationCond.showHighLevel &&
                    <Text>{ServiceLocationTypeMap.get(offer.serviceLocationHighLevelType)}</Text>}
            </div>
        </div>
    );
}

const practitionerMedicatedHoursRender = (offer: Offer, referral: Referral, referralHours: Record<RequiredHourFields, number>, shouldShowGroupCareHours: boolean, hide: boolean) => {
    if (hide) {
        return null;
    }
    const isNotCompassBehavioral = !(referral?.serviceTypes ?? []).includes(ServiceType.Compass_Behavioral);
    const has3TierABACombinedService = (referral?.serviceTypes ?? []).includes(ServiceType.ThreeTierOrParentTraining) || (referral?.serviceTypes ?? []).includes(ServiceType.ThreeTierOrParentLed);

    return (
        <div className={"grid grid-cols-2"}>
            {
                has3TierABACombinedService &&
                <DateView label='Start Date Practitioner Mediated'
                          dateStr={offer.startDatePractMediatedTreatment}/>
            }
            {
                !has3TierABACombinedService && <div/>
            }
            <FormField label={'Service Location:'} paddingY={16}>
                <Text>{ServiceLocationTypeMap.get(offer.serviceLocationType)}</Text>
            </FormField>
            {
                isNotCompassBehavioral && (<>
                    <HourView label='Paraprofessional Hours:'
                              actualHour={offer.directCareHours}
                              expectedHour={referralHours.directCareHours}
                    />
                    <div className={'flex items-center'}>
                        <Text>{ServiceLocationTypeMap.get(offer.serviceLocationParaprofessionalType)}</Text>
                    </div>
                    <HourView label='Mid-level Supervision Hours:'
                              actualHour={offer.midLevelSupervisionHours}
                              expectedHour={referralHours.midLevelHours}
                    />
                    <div className={'flex items-center'}>
                        <Text>{ServiceLocationTypeMap.get(offer.serviceLocationMidLevelType)}</Text>
                    </div>
                </>)
            }
            <HourView label='High-level Supervision Hours:'
                      actualHour={offer.highLevelSupervisionHours}
                      expectedHour={referralHours.highLevelHours}
            />
            <div className={'flex items-center'}>
                <Text>{ServiceLocationTypeMap.get(offer.serviceLocationHighLevelType)}</Text>
            </div>
            {
                shouldShowGroupCareHours &&
                <>
                    <HourView label='Group Care Hours:'
                              actualHour={offer.groupCareHours}
                              expectedHour={referralHours.groupCareHours}
                    />

                    <div className={'flex items-center'}>
                        <Text>{ServiceLocationTypeMap.get(offer.serviceLocationGroupCareType)}</Text>
                    </div>
                </>
            }
        </div>
    );
}

const caregiverAndPractionerMedicatedRender = (offer: Offer, referralHours: Record<RequiredHourFields, number>, referral: Referral, shouldShowGroupHours: boolean) => {
    if (!isTreatmentOfferedCaregiverAndPractitioner(offer.treatmentOffered)) {
        return null;
    }
    return (
        <div>
            <div>
                <DateView label='Start Date Caregiver Mediated'
                          dateStr={offer.startDateCaregiverMediatedTreatment}/>
                <HourView label='Mid-Level Supervision Hours:'
                          actualHour={offer.caregiverMediatedMidLevelHours}
                          expectedHour={referralHours.careMediatedMidHours}
                />
                <HourView label='High-Level Supervision Hours:'
                          actualHour={offer.caregiverMediatedHighLevelHours}
                          expectedHour={referralHours.careMediatedHighHours}
                />
            </div>
            {practitionerMedicatedHoursRender(offer, referral, referralHours, shouldShowGroupHours, false)}
        </div>
    );
}

const staffingPlanRender = ({draftStaffingPlan}: Offer) => {
    if (!draftStaffingPlan) return null;

    const {
        fullOfferStartDate,
        // reviewedAndApprovedByClientFamilyDate,
        offeredParaprofessionalHours,
        offeredMidLevelHours,
        offeredHighLevelHours,
        offeredGroupCareHours,
        comments
    } = draftStaffingPlan;

    return (
        <div className='border-2 p-2 w-7/12 pb-4'>
            <div className='font-bold text-base'>Staffing Plan</div>
            <DateView label='Full Offer Start Date' dateStr={fullOfferStartDate}/>
            {/*<DateView label='Date Staffing Plan Reviewed by Client Family' showDaysFromNow={false}*/}
            {/*          dateStr={reviewedAndApprovedByClientFamilyDate}/>*/}
            {offeredParaprofessionalHours !== null &&
                <HourView label='Paraprofessional Hours:' actualHour={offeredParaprofessionalHours}
                          expectedHour={null}/>}
            <HourView label='Mid-Level Supervision Hours:' actualHour={offeredMidLevelHours} expectedHour={null}/>
            <HourView label='High-Level Supervision Hours:' actualHour={offeredHighLevelHours} expectedHour={null}/>
            {offeredGroupCareHours !== null &&
                <HourView label='Group Care Hours:' actualHour={offeredGroupCareHours} expectedHour={null}/>}
            {!isEmpty(comments) && (
                <FormField label='Comments' marginTop={8}>
                    <Pre wordBreak='break-word' whiteSpace='break-spaces' wordWrap='break-word'>{comments}</Pre>
                </FormField>
            )}
        </div>
    );
};
// BHPNPP-1944
// group 4
// const groupComboRender = (offer: Offer, referralHours: Record<RequiredHourFields, number>) => {
//     return (
//         <div>
//             <HourView label='Mid-Level Supervision Hours:'
//                       actualHour={offer.caregiverMediatedMidLevelHours}
//                       expectedHour={referralHours.careMediatedMidHours}
//             />
//             <HourView label='High-Level Supervision Hours'
//                       actualHour={offer.caregiverMediatedHighLevelHours}
//                       expectedHour={referralHours.careMediatedHighHours}
//             />
//             <HourView label='Group Care Hours:'
//                       actualHour={offer.groupCareHours}
//                       expectedHour={referralHours.groupCareHours}
//             />
//         </div>
//     );
// };
